import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Preferences } from "@capacitor/preferences";
import { observerAuth } from "./firebaseAuth";
import { observerBackendOrigin } from "./runtimeConfig";

const API = `${observerBackendOrigin}/api/observer/practitioner/devices`;
const DEVICE_KEY = "observer_practitioner_device_id";
const PERMISSION_KEY = "observer_push_permission_state";
let initialized = false;

type PermissionRecord = {
  declineCount: number;
  declinedAt?: string;
  nextEligiblePromptAt?: string;
};

async function deviceID() {
  const existing = await Preferences.get({ key: DEVICE_KEY });
  if (existing.value) return existing.value;
  const id =
    globalThis.crypto?.randomUUID?.() ||
    `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  await Preferences.set({ key: DEVICE_KEY, value: id });
  return id;
}

async function authHeaders() {
  const user = observerAuth().currentUser;
  if (!user || !user.emailVerified)
    throw new Error("verified_firebase_account_required");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${await user.getIdToken()}`,
  };
}

async function request(path = "", init: RequestInit = {}) {
  const headers = await authHeaders();
  const response = await fetch(`${API}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers || {}) },
  });
  if (!response.ok) throw new Error(`push_api_${response.status}`);
  if (response.status === 204) return null;
  return response.json();
}

function timezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

async function permissionRecord(): Promise<PermissionRecord> {
  const value = await Preferences.get({ key: PERMISSION_KEY });
  try {
    return value.value ? JSON.parse(value.value) : { declineCount: 0 };
  } catch {
    return { declineCount: 0 };
  }
}

async function savePermissionRecord(record: PermissionRecord) {
  await Preferences.set({ key: PERMISSION_KEY, value: JSON.stringify(record) });
}

async function recordDecision(decision: "granted" | "denied") {
  const id = await deviceID();
  const result = await request("/permission-decision", {
    method: "POST",
    body: JSON.stringify({
      deviceId: id,
      platform: Capacitor.getPlatform(),
      decision,
      timezone: timezone(),
    }),
  });
  const local: PermissionRecord =
    decision === "denied"
      ? {
          declineCount: result.declineCount,
          declinedAt: new Date().toISOString(),
          nextEligiblePromptAt: result.nextEligiblePromptAt,
        }
      : { declineCount: result.declineCount || 0 };
  await savePermissionRecord(local);
  return result;
}

async function registerToken(token?: string) {
  const fcmToken = token || (await FirebaseMessaging.getToken()).token;
  if (!fcmToken) return;
  await request("", {
    method: "POST",
    body: JSON.stringify({
      deviceId: await deviceID(),
      token: fcmToken,
      platform: Capacitor.getPlatform(),
      permissionStatus: "granted",
      timezone: timezone(),
      quietHoursStart: "22:00",
      quietHoursEnd: "08:00",
    }),
  });
}

export async function initializeMobilePush() {
  if (initialized || !Capacitor.isNativePlatform()) return;
  initialized = true;
  await FirebaseMessaging.addListener("tokenReceived", ({ token }) => {
    void registerToken(token);
  });
  await FirebaseMessaging.addListener(
    "notificationActionPerformed",
    ({ notification }) => {
      const data = notification.data as Record<string, string> | undefined;
      const publicID = String(data?.entity_public_id || "");
      if (publicID)
        window.location.assign(`/profile/${encodeURIComponent(publicID)}`);
    },
  );
  await FirebaseMessaging.addListener(
    "notificationReceived",
    ({ notification }) => {
      const data = notification.data as Record<string, string> | undefined;
      window.dispatchEvent(
        new CustomEvent("observer:push-received", {
          detail: { eventType: data?.event_type },
        }),
      );
    },
  );
  await App.addListener("appStateChange", async ({ isActive }) => {
    if (!isActive) return;
    const status = await FirebaseMessaging.checkPermissions();
    if (status.receive === "granted")
      await registerToken().catch(() => undefined);
  });
  const status = await FirebaseMessaging.checkPermissions();
  if (status.receive === "granted")
    await registerToken().catch(() => undefined);
}

// The only caller is the successful claim-verification transition. It never
// runs on first open and never creates seeker notification infrastructure.
export async function requestPractitionerPushAfterClaimVerification() {
  if (!Capacitor.isNativePlatform()) return "web";
  const id = await deviceID();
  const status = await FirebaseMessaging.checkPermissions();
  if (status.receive === "granted") {
    await recordDecision("granted");
    await registerToken();
    return "granted";
  }
  const local = await permissionRecord();
  if (
    local.nextEligiblePromptAt &&
    new Date(local.nextEligiblePromptAt) > new Date()
  )
    return "cooldown";
  const eligibility = await request(
    `/permission-eligibility?deviceId=${encodeURIComponent(id)}`,
  );
  if (!eligibility.eligible) return eligibility.reason || "cooldown";
  window.dispatchEvent(new CustomEvent("observer:push-permission-prompt"));
  return "prompted";
}

export async function acceptPractitionerPushPermission() {
  if (!Capacitor.isNativePlatform()) return false;
  const status = await FirebaseMessaging.requestPermissions();
  if (status.receive !== "granted") {
    await recordDecision("denied");
    return false;
  }
  await recordDecision("granted");
  await registerToken();
  return true;
}

export async function declinePractitionerPushPermission() {
  if (!Capacitor.isNativePlatform()) return;
  await recordDecision("denied");
}

export async function unregisterPractitionerDevice() {
  if (!Capacitor.isNativePlatform()) return;
  const id = await deviceID();
  await request(`/${encodeURIComponent(id)}`, { method: "DELETE" }).catch(
    () => undefined,
  );
  await FirebaseMessaging.deleteToken().catch(() => undefined);
}
